import { Component, ElementRef, signal, computed, inject, OnInit, viewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Role as Roles } from './role.model';
import { RoleService } from './role.service';
import { Loader } from '../../shared/components/loader/loader';
import { PageHeader } from '../../shared/components/page-header/page-header';
import { NotificationDialog } from '../../shared/components/notification-dialog/notification-dialog';
import { ConfirmDialog } from '../../shared/components/confirm-dialog/confirm-dialog';
import { DataTable, TableColumn } from '../../shared/components/data-table/data-table';
import { Button } from '../../shared/components/button/button';

@Component({
  selector: 'app-role',
  imports: [CommonModule, FormsModule, Loader, PageHeader, NotificationDialog, ConfirmDialog, DataTable, Button],
  templateUrl: './role.html',
  styleUrl: './role.scss',
})
export class Role implements OnInit {
  isCurrentUserSuperAdmin = signal(true);
  protected readonly Math = Math;
  notificationMessage = signal('');
  notificationType = signal<'success' | 'error'>('success');
  isNotificationVisible = signal(false);
  roles = signal<Roles[]>([]);
  filteredRoles = signal<Roles[]>([]);
  currentPage = signal(1);
  pageSize = 10;
  selectedRole = signal<Roles | null>(null);
  isEditRole = signal(false);
  isAddRole = signal(false);

  roleName = signal('');
  roleDescription = signal('');
  private readonly descriptionInput = viewChild<ElementRef<HTMLTextAreaElement>>('descriptionInput');
  isActive = signal(true);
  
  reportsView = signal(false);
  viewKB = signal(false);
  manageKB = signal(false);

  showDeleteModal = signal(false);
  roleToDelete = signal<Roles | null>(null);

  isLoading = signal(false);
  private loadingCount = 0;

  private startLoading(): void { this.loadingCount++; this.isLoading.set(true); }
  private stopLoading(): void { this.loadingCount = Math.max(0, this.loadingCount - 1); if (this.loadingCount === 0) this.isLoading.set(false); }

  tableColumns: TableColumn[] = [
    { key: 'roleName', label: 'Role Name', sortable: true, width: '30%' },
    { key: 'roleDescription', label: 'Description', sortable: true, width: '30%' },
    { key: 'active', label: 'Active', align: 'center', sortable: true, width: '20%' },
  ];

  searchCriteria = {
    roleName: '',
    active: '' as 'Yes' | 'No' | '',
  };

  private roleService = inject(RoleService);

  ngOnInit(): void {
    this.fetchRoles();
  }

  fetchRoles(): void {
    this.onSearch();
  }

  onSearch(): void {
    this.startLoading();
    this.roleService.getRoles().subscribe({
      next: (roles) => {
        console.log('Roles loaded from backend:', roles);
        const sorted = [...roles].sort((a, b) =>
          String(a?.roleName ?? '').localeCompare(String(b?.roleName ?? '')),
        );
        this.filteredRoles.set(sorted);
        this.currentPage.set(1);
        this.roles.set(sorted);
        if (sorted.length > 0) {
          this.onSelectRole(sorted[0]);
        }
        this.stopLoading();
      },
      error: (err) => {
        console.error('Error searching roles:', err);
        this.showNotification('Search failed. Please try again.', 'error');
        this.stopLoading();
      }
    });
  }

  showNotification(message: string, type: 'success' | 'error' = 'success'): void {
    this.notificationMessage.set(message);
    this.notificationType.set(type);
    this.isNotificationVisible.set(true);
  }

  paginatedRoles = computed(() => {
    const startIndex = (this.currentPage() - 1) * this.pageSize;
    return this.filteredRoles().slice(startIndex, startIndex + this.pageSize);
  });

  totalPages = computed(() => {
    return Math.ceil(this.filteredRoles().length / this.pageSize) || 1;
  });

  hasAtLeastOnePermission = computed(() => {
    return (
      this.reportsView() ||
      this.viewKB() ||
      this.manageKB()
    );
  });

  isFormValid = computed(() => {
    return (
      this.roleName().trim().length > 0 &&
      this.roleName().trim().length <= 100 &&
      this.roleDescription().trim().length <= 500 &&
      this.hasAtLeastOnePermission()
    );
  });

  onPageChange(page: number): void {
    this.currentPage.set(page);
    const roles = this.paginatedRoles();
    if (roles.length > 0) {
      this.onSelectRole(roles[0]);
    }
  }

  onSelectRole(role: Roles): void {
    const id = role.id;
    console.log('onSelectRole called with role:', role, 'roleId:', id);
    
    if (id) {
      this.startLoading();
      this.roleService.getRoleById(id).subscribe({
        next: (roleDetails) => {
          console.log('Role details loaded:', roleDetails);
          this.selectedRole.set(roleDetails);
          this.isEditRole.set(false);
          this.isAddRole.set(false);
          this.loadRoleDetails(roleDetails);
          this.stopLoading();
        },
        error: (err) => {
          console.error('Error loading role details:', err);
          this.selectedRole.set(role);
          this.loadRoleDetails(role);
          this.stopLoading();
        }
      });
    } else {
      console.warn('Role has no roleId or id:', role);
      this.selectedRole.set(role);
      this.loadRoleDetails(role);
    }
  }

  private loadRoleDetails(role: Roles): void {
    this.roleName.set(role.roleName);
    this.roleDescription.set(role.roleDescription);
    this.isActive.set(role.active === 'Yes');
      
    this.reportsView.set(role.permissions?.reports?.view || false);
    this.manageKB.set(role.permissions?.knowledgeBase?.manage || false);
    this.viewKB.set(this.manageKB() || role.permissions?.knowledgeBase?.view || false);
  }

  onCreateRole(): void {
    this.isAddRole.set(true);
    this.isEditRole.set(true);
    this.selectedRole.set(null);
    this.roleName.set('');
    this.roleDescription.set('');
    this.isActive.set(true);
    this.reportsView.set(false);
    this.viewKB.set(false);
    this.manageKB.set(false);
  }

  private isDuplicateRoleName(roleName: string): boolean {
    const normalizedName = roleName.trim().toLowerCase();
    const currentRoleId = this.selectedRole()?.id;

    return this.roles().some(role =>
      role.roleName.trim().toLowerCase() === normalizedName &&
      role.id !== currentRoleId
    );
  }

  onEditRole(): void {
    this.isEditRole.set(true);
  }

  onDeleteRole(role: Roles): void {
    const id = role.id;
    console.log('onDeleteRole called with role:', role, 'roleId:', id);
    
    if (id) {
      this.roleService.checkRoleAssignment(id).subscribe({
        next: (isAssigned) => {
          if (isAssigned) {
            this.showNotification('Cannot delete role that is assigned to users. Remove all user assignments first.', 'error');
          } else {
            console.log('Setting delete modal to show');
            this.roleToDelete.set(role);
            this.showDeleteModal.set(true);
          }
        },
        error: () => {
          console.log('Error checking role assignment, showing delete modal anyway');
          this.roleToDelete.set(role);
          this.showDeleteModal.set(true);
        }
      });
    } else {
      console.error('Cannot delete role - no roleId or id found');
      this.showNotification('Cannot delete role - invalid role ID', 'error');
    }
  }

  cancelDelete(): void {
    this.showDeleteModal.set(false);
    this.roleToDelete.set(null);
  }

  confirmDelete(): void {
    const role = this.roleToDelete();
    const id = role?.id;
    console.log('confirmDelete called with role:', role, 'roleId:', id);
    
    if ( role && id ) {
      this.startLoading();
            this.roleService.deleteRole(id).subscribe({
        next: () => {
          const updatedRoles = this.roles().filter(r => (r.id) !== id);
          const updatedFiltered = this.filteredRoles().filter(r => (r.id) !== id);
          this.roles.set(updatedRoles);
          this.filteredRoles.set(updatedFiltered);
          
          this.showDeleteModal.set(false);
          this.roleToDelete.set(null);
          this.showNotification('Role deleted successfully.', 'success');
          
          // Select first role if any remain
          if (updatedFiltered.length > 0) {
            this.onSelectRole(updatedFiltered[0]);
          } else {
            this.selectedRole.set(null);
            this.isEditRole.set(false);
            this.isAddRole.set(false);
          }
          
          this.stopLoading();
        },
        error: (err) => {
          console.error('Error deleting role:', err);
          const errorMessage = err?.error?.message || 'Failed to delete role. Please try again.';
          this.showNotification(errorMessage, 'error');
          this.showDeleteModal.set(false);
          this.stopLoading();
        }
      });
    }
  }

  onSave(): void {
    const name = this.roleName().trim();
    if (!name) {
      this.showNotification('Role Name is required.', 'error');
      return;
    }
    if (this.isDuplicateRoleName(name)) {
      this.showNotification('Role name must be unique.', 'error');
      return;
    }
    if (name.length > 100) {
      this.showNotification('Role Name cannot exceed 100 characters.', 'error');
      return;
    }
    if (this.roleDescription().trim().length > 500) {
      this.showNotification('Role Description cannot exceed 500 characters.', 'error');
      return;
    }
    if (!this.hasAtLeastOnePermission()) {
      this.showNotification('At least one permission must be selected.', 'error');
      return;
    }

    const newRole: Roles = {
      id: this.selectedRole()?.id,
      roleName: name,
      roleDescription: this.roleDescription().trim(),
      active: this.isActive() ? 'Yes' : 'No',
      permissions: {
        reports: {
          view: this.reportsView(),
        },
        knowledgeBase: {
          view: this.viewKB(),
          manage: this.manageKB(),
        },
      },
    };

    if (this.isAddRole()) {
      this.startLoading();
      this.roleService.createRole(newRole).subscribe({
        next: (roleWithId) => {
          const updatedRoles = [...this.roles(), roleWithId].sort((a, b) =>
            String(a?.roleName ?? '').localeCompare(String(b?.roleName ?? ''))
          );
          this.roles.set(updatedRoles);
          this.filteredRoles.set(updatedRoles);
          this.isAddRole.set(false);
          this.isEditRole.set(false);
          this.selectedRole.set(roleWithId);
          this.onSelectRole(roleWithId);
          this.showNotification('Role created successfully.', 'success');
          this.stopLoading();
        },
        error: (err) => {
          console.error('Error creating role:', err);
          const errorMessage = err.error?.message || err.message || 'Failed to create role. Please try again.';
          this.showNotification(errorMessage, 'error');
          this.stopLoading();
        }
      });
    } else {
      const selected = this.selectedRole();
      const selectedRoleId = selected?.id;
      if (selected && selectedRoleId && selected.roleName) {
        this.startLoading();
        this.roleService.updateRole(selectedRoleId, newRole).subscribe({
          next: (updatedRole) => {
            const updatedRoles = this.roles().map(r => {
              const rId = r.id;
              return rId === selectedRoleId ? updatedRole : r;
            }).sort((a, b) =>
              String(a?.roleName ?? '').localeCompare(String(b?.roleName ?? '')));
            const updatedFiltered = this.filteredRoles().map(r => {
              const rId = r.id;
              return rId === selectedRoleId ? updatedRole : r;
            }).sort((a, b) =>
              String(a?.roleName ?? '').localeCompare(String(b?.roleName ?? '')));
            this.roles.set(updatedRoles);
            this.filteredRoles.set(updatedFiltered);
            this.isAddRole.set(false);
            this.isEditRole.set(false);
            this.selectedRole.set(updatedRole);
            this.onSelectRole(updatedRole);
            this.showNotification('Role updated successfully.', 'success');
            this.stopLoading();
          },
          error: (err) => {
            console.error('Error updating role:', err);
            const errorMessage = err?.error?.message || err?.message || 'Failed to update role. Please try again.';
            this.showNotification(errorMessage, 'error');
            this.stopLoading();
          }
        });
      } else {
        console.error('Cannot update role - invalid role selection:', selected);
        this.showNotification('Cannot update role - invalid role ID. Please refresh the role list.', 'error');
      }
    }
  }

  onCancel(): void {
    this.descriptionInput()?.nativeElement.style.removeProperty('height');
    if (this.isAddRole()) {
      const firstRole = this.filteredRoles().length > 0 ? this.filteredRoles()[0] : null;
      this.isAddRole.set(false);
      this.isEditRole.set(false);
      if (firstRole) {
        this.onSelectRole(firstRole);
      } else {
        this.selectedRole.set(null);
      }
    } else if (this.selectedRole()) {
      this.isEditRole.set(false);
      this.onSelectRole(this.selectedRole()!);
    }
  }

  onFormKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && event.ctrlKey) {
      if (this.isFormValid()) {
        this.onSave();
      }
    } else if (event.key === 'Escape') {
      this.onCancel();
    }
  }

  onManageKBChange(value: boolean): void {
    this.manageKB.set(value);
    if (value) {
      this.viewKB.set(true);
    }
  }
}
